import type { PropsWithChildren } from "react";
import { Component, ErrorInfo, FC } from "react";
import { Box } from "@material-ui/core";
import { PostHog } from "posthog-js";

import { EXCHANGE_RUNTIME_ERROR } from "services/postHog/constants";

import ErrorDisplay from "./ErrorDisplay";

interface Props {
  posthog: PostHog | null; // ErrorBoundary is rendered above the PostHogProvider in Root/index.tsx so we need to allow for null
  CustomContainer?: FC<PropsWithChildren>;
  scrollTop?: boolean;
}

interface State {
  hasError: boolean;
  isScrolledTop: boolean;
}

class ErrorBoundary extends Component<PropsWithChildren<Props>, State> {
  constructor(props: PropsWithChildren<Props>) {
    super(props);
    this.state = { hasError: false, isScrolledTop: false };
  }

  static getDerivedStateFromError() {
    return {
      hasError: true,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.props.posthog?.capture(EXCHANGE_RUNTIME_ERROR, {
      errorMessage: error.toString(),
      trace: JSON.stringify(errorInfo),
    });
  }

  componentDidUpdate() {
    if (
      this.state.hasError &&
      !!this.props.scrollTop &&
      !this.state.isScrolledTop
    ) {
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        this.setState({ isScrolledTop: true });
      }, 200);
    }
  }

  render() {
    if (this.state.hasError) {
      if (!!this.props.CustomContainer) {
        return (
          <this.props.CustomContainer>
            <ErrorDisplay />
          </this.props.CustomContainer>
        );
      }
      return (
        <Box
          height="80vh"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
        >
          <ErrorDisplay />
        </Box>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
